//#region Imports

import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { ContentType } from '../../../../shared/models/enums/content-type.enum';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { CourseContentTypeVideoComponent } from '../content-types/video/course-content-type-video.component';
import { CourseContentTypeTextEditorComponent } from '../content-types/text-editor/course-content-type-text-editor.component';
import { CourseContentTypeYoutubeComponent } from '../content-types/youtube/course-content-type-youtube.component';
import { CourseContentTypeVimeoComponent } from '../content-types/vimeo/course-content-type-vimeo.component';
import { CourseContentTypeWebPageComponent } from '../content-types/web-page/course-content-type-web-page.component';
import { VideoEmbedPreview } from '../../../../shared/helpers/video-embed.helper';
import { ClassContentUploadState, ClassForm } from '../../../../shared/models/interfaces/forms/course-form.interface';

//#endregion

interface CourseContentFormOption {
  id: ContentType;
  title: string;
  description: string;
  icon: string;
}

const DEFAULT_CONTENT_TYPE_OPTIONS: ReadonlyArray<CourseContentFormOption> = [
  {
    id: ContentType.VIDEO,
    title: 'Video',
    description: 'Envie um video diretamente para a plataforma',
    icon: 'videocam',
  },
  {
    id: ContentType.RICH_TEXT,
    title: 'Editor de texto',
    description: 'Curso baseado em texto com imagens e formatacao',
    icon: 'notes',
  },
  {
    id: ContentType.YOUTUBE,
    title: 'YouTube',
    description: 'Incorpore um video hospedado no YouTube',
    icon: 'smart_display',
  },
  {
    id: ContentType.VIMEO,
    title: 'Vimeo',
    description: 'Incorpore um video hospedado no Vimeo',
    icon: 'video_library',
  },
  {
    id: ContentType.HTML,
    title: 'Pagina Web (HTML / WebGL)',
    description: 'Conteudo personalizado usando HTML',
    icon: 'code',
  },
];

@Component({
  selector: 'l-course-content-form',
  standalone: true,
  templateUrl: './course-content-form.component.html',
  imports: [
    IconComponent,
    NgClass,
    ReactiveFormsModule,
    CourseContentTypeVideoComponent,
    CourseContentTypeTextEditorComponent,
    CourseContentTypeYoutubeComponent,
    CourseContentTypeVimeoComponent,
    CourseContentTypeWebPageComponent,
  ],
})
export class CourseContentFormComponent {

  //#region Inputs

  @Input({ required: true })
  public form!: FormGroup<ClassForm>;

  @Input()
  public uploadState: ClassContentUploadState = {};

  @Input()
  public options: ReadonlyArray<CourseContentFormOption> = DEFAULT_CONTENT_TYPE_OPTIONS;

  //#endregion

  //#region Outputs

  @Output()
  public readonly uploadStateChange = new EventEmitter<ClassContentUploadState>();

  //#endregion

  //#region Public Properties

  public pendingType: ContentType | null = null;

  public readonly contentType = ContentType;

  //#endregion

  //#region Getters

  public get selectedType(): ContentType | null {
    return this.form.controls.type.value;
  }

  public get selectedOption(): CourseContentFormOption | undefined {
    return this.options.find((option) => option.id === this.selectedType);
  }

  //#endregion

  //#region Public Methods

  public isSelected(optionId: ContentType): boolean {
    if (this.selectedType) {
      return this.selectedType === optionId;
    }

    return this.pendingType === optionId;
  }

  public onPreviewType(optionId: ContentType): void {
    this.pendingType = optionId;
  }

  public onSelect(optionId: ContentType): void {
    this.pendingType = optionId;
    this.form.controls.type.setValue(optionId);
  }

  public onChangeType(): void {
    this.pendingType = null;
    this.form.controls.type.setValue(null);
  }

  public onVideoFileStateChange(fileState: { file: File | null; fileSizeLabel: string }): void {
    this.patchUploadState({
      video: {
        file: fileState.file,
        fileSizeLabel: fileState.fileSizeLabel,
      },
    });
    this.form.controls.videoFileName.setValue(fileState.file?.name ?? '');
  }

  public onThumbnailFileChange(file: File | null): void {
    this.patchUploadState({
      thumbnail: { file },
    });
  }

  public onYoutubePreviewChange(preview: VideoEmbedPreview | null): void {
    this.patchUploadState({ youtube: { preview } });
  }

  public onVimeoPreviewChange(preview: VideoEmbedPreview | null): void {
    this.patchUploadState({ vimeo: { preview } });
  }

  public onWebPageFileStateChange(fileState: { file: File | null; fileSizeLabel: string }): void {
    this.patchUploadState({
      webPage: {
        file: fileState.file,
        fileSizeLabel: fileState.fileSizeLabel,
      },
    });
    this.form.controls.webPageFileName.setValue(fileState.file?.name ?? '');
  }

  //#endregion

  //#region Private Methods

  private patchUploadState(partial: ClassContentUploadState): void {
    this.uploadStateChange.emit({
      ...this.uploadState,
      ...partial,
    });
  }

  //#endregion

}
